// src/components/SiteLayout.jsx
import React from "react";
import { Link, useLocation } from "react-router-dom";

const SUPPORT_EMAIL = "support@starepochlabs.com";
const MAILING_ADDRESS_ONE_LINE = "30 N Gould St Ste N, Sheridan, WY 82801";

const navItems = [
  { label: "Home", to: "/" },
  { label: "Company", to: "/company" },
  { label: "Trust Center", to: "/trust" },
  { label: "Privacy", to: "/privacy" },
  { label: "Terms", to: "/terms" },
];

function NavLink({ to, children }) {
  const loc = useLocation();
  const active = loc.pathname === to;

  return (
    <Link
      to={to}
      className={[
        "text-sm font-medium transition-colors no-underline hover:no-underline",
        "px-3 py-2 rounded-lg",
        active
          ? "text-slate-900 bg-slate-900/5"
          : "text-slate-700 hover:text-slate-900 hover:bg-slate-900/5",
      ].join(" ")}
    >
      {children}
    </Link>
  );
}

export default function SiteLayout({ children }) {
  return (
    <div className="min-h-screen bg-[#070A12] text-slate-100">
      {/* Background glow */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -top-40 left-1/2 h-[520px] w-[520px] -translate-x-1/2 rounded-full bg-blue-600/20 blur-[120px]" />
        <div className="absolute top-40 left-10 h-[420px] w-[420px] rounded-full bg-cyan-400/10 blur-[120px]" />
        <div className="absolute bottom-0 right-0 h-[520px] w-[520px] rounded-full bg-indigo-500/10 blur-[120px]" />
        <div className="absolute inset-0 bg-gradient-to-b from-white/[0.03] via-transparent to-black/40" />
      </div>

      {/* Navbar */}
      <header className="sticky top-0 z-50 border-b border-white/10 bg-white backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-3">
            <img
              src="/favicon/favicon-96x96.png"
              alt="StarEpoch Labs"
              className="h-9 w-9 rounded-xl shadow-sm"
              loading="eager"
            />
            <div className="leading-tight">
              <div className="text-sm font-semibold tracking-wide text-slate-900">
                StarEpoch Labs
              </div>
              <div className="text-[11px] text-slate-600">
                Privacy-first networking
              </div>
            </div>
          </Link>

          <nav className="hidden items-center gap-1 md:flex">
            {navItems.map((n) => (
              <NavLink key={n.to} to={n.to}>
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <Link
              to="/contact"
              className="hidden rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-900 transition hover:bg-slate-50 md:inline-flex"
            >
              Contact
            </Link>

            <Link
              to="/support"
              className="inline-flex items-center justify-center rounded-xl bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-red-500 active:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-400/60 focus:ring-offset-2 focus:ring-offset-white"
            >
              Support
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="relative mx-auto">{children}</main>

      {/* Footer */}
      <footer className="relative border-t border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-6xl px-6 py-12">
          <div className="grid gap-10 md:grid-cols-3">
            {/* Brand */}
            <div>
              <div className="text-base font-semibold text-slate-900">
                StarEpoch Labs LLC
              </div>
              <div className="mt-2 text-sm leading-relaxed text-slate-600">
                We build privacy-first networking software and security primitives for modern applications.
                Transparent practices, security-first engineering, and reliable support.
              </div>
            </div>

            {/* Links */}
            <div className="text-sm">
              <div className="font-semibold text-slate-900">Legal & Policies</div>
              <div className="mt-3 grid gap-2 text-slate-700">
                <Link className="hover:text-slate-900" to="/trust">Trust Center</Link>
                <Link className="hover:text-slate-900" to="/privacy">Privacy Policy</Link>
                <Link className="hover:text-slate-900" to="/terms">Terms of Service</Link>
                <Link className="hover:text-slate-900" to="/support">Support</Link>
                <Link className="hover:text-slate-900" to="/contact">Contact</Link>
              </div>
            </div>

            {/* Contact */}
            <div className="text-sm">
              <div className="font-semibold text-slate-900">Contact</div>

              <div className="mt-3 space-y-2 text-slate-700">
                <div>
                  Support:{" "}
                  <a className="text-red-700 hover:text-red-600" href={`mailto:${SUPPORT_EMAIL}`}>
                    {SUPPORT_EMAIL}
                  </a>
                </div>
                <div>
                  Mailing address:{" "}
                  <span className="text-slate-700">{MAILING_ADDRESS_ONE_LINE}</span>
                </div>
                <div className="text-xs text-slate-500">
                  Response time: typically within 1–2 business days.
                </div>
              </div>
            </div>
          </div>

          <div className="mt-10 flex flex-col gap-3 border-t border-slate-200 pt-6 text-xs text-slate-500 md:flex-row md:items-center md:justify-between">
            <div>© {new Date().getFullYear()} StarEpoch Labs LLC. All rights reserved.</div>
            <div className="flex flex-wrap gap-4">
              <Link className="hover:text-slate-700" to="/privacy">Privacy</Link>
              <Link className="hover:text-slate-700" to="/terms">Terms</Link>
              <Link className="hover:text-slate-700" to="/trust">Trust</Link>
            </div>
          </div>
        </div>
      </footer>

    </div>
  );
}
